// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader, View } from 'reshaped';

import { withPageMeta } from '@gandi/app-runner';
import {
  addCartProduct,
  selectCartUUID,
  getCart,
  selectTotalProducts,
} from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { useResourceFetch } from '@gandi/resource/react';
import { useTranslator } from '@gandi/translate';

import { useUpdateCartProductsOwnership } from 'src/Cart/modules/cartOwnership';
import { submitSearch } from 'src/DomainCreate/modules/actions/suggestions';
import type { ExtendedSuggestion } from 'src/DomainCreate/types';
import DomainIntro from 'src/WebHosting/components/DomainIntro';
import DomainSearch from 'src/WebHosting/components/DomainSearch';
import { selectAvailableResultCount } from 'src/WebHosting/modules/selectors';
import { Layout } from 'src/components/Layout';
import { useNotifications } from 'src/hooks';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { stopSSE } from 'src/modules/app/sse';
import {
  selectStudioFlowExpectedStep,
  selectIsStudioUser,
} from 'src/modules/gandi-studio';
import {
  selectShopCurrent,
  selectHasMissingOrgInfo,
} from 'src/modules/organization/selectors';
import type { Dispatch, GetState } from 'src/types';

import { getStepAfterDomainChoice, getStepUrl } from '../helpers';
import { shsResource } from '../modules/instances';

import styles from './Convert.module.css';

export type Params = {
  vhost: string,
};

function StudioConvert(): React.MixedElement | null {
  const { __ } = useTranslator();
  const { vhost } = useParams<Params>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const current = useAppSelector(selectShopCurrent);

  const studioFirstBuyFlagActivate = useFeatureFlag('FF_STUDIO_FIRST_BUY');
  const isStudioUser = useAppSelector(selectIsStudioUser);
  const missingOrgInfo = useAppSelector(state =>
    selectHasMissingOrgInfo(state, { orgId: current?.id }),
  );
  const cartTotalProducts = useAppSelector(selectTotalProducts);

  const search = searchParams.get('search');

  const updateCartProductsOwnership = useUpdateCartProductsOwnership();

  const { notifyMaybeApiError } = useNotifications();

  const fetchParams = React.useMemo(() => ({ fqdn: vhost }), [vhost]);

  const { summaries, isFailure, isFetching, isLoaded } = useResourceFetch(
    shsResource,
    fetchParams,
  );

  const domainCount = useAppSelector(selectAvailableResultCount);
  const hasEnoughDomains = domainCount > 5; // minimum result count to trigger the scroll

  const redirectTo = useAppSelector(state =>
    selectStudioFlowExpectedStep(state, studioFirstBuyFlagActivate),
  );

  // Redirect if the user doesn't have Gandi Studio or has already purchased a Gandi Studio product.
  React.useEffect(() => {
    if (isLoaded && (!summaries?.length || !summaries[0].trial)) {
      navigate('/domain/suggest', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redirect if the Gandi Studio user has already chosen a domain.
  React.useEffect(() => {
    if (redirectTo) navigate(redirectTo, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // ------------------------

  React.useEffect(() => {
    if (search) {
      dispatch(submitSearch(search));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const targetRef = React.useRef(null);
  const hasScrolledRef = React.useRef(false);

  React.useEffect(() => {
    if (!search || !hasEnoughDomains || hasScrolledRef.current) {
      return;
    }

    hasScrolledRef.current = true;
    requestAnimationFrame(() => {
      targetRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }, [search, hasEnoughDomains]);

  React.useEffect(() => {
    if (isFailure || summaries?.length === 0) {
      dispatch(notifyDanger(__('Unexpected error occurred')));
      navigate('/domain/suggest', { replace: true });
    }
  }, [isFailure, summaries?.length, dispatch, navigate, __, vhost]);

  if (isFetching || !summaries) {
    return (
      <View justify="center" direction="row">
        <Loader size="large" ariaLabel={__('Loading...')} />
      </View>
    );
  }

  if (isFailure || summaries.length === 0) return null;
  const handleChooseDomain = async (
    suggestion: ExtendedSuggestion,
  ): Promise<void> => {
    stopSSE();
    const { tld, fqdn } = suggestion;
    // The FQDN of a vhost is unique in the DB.
    const identifier = summaries[0].id; // protected by early failing mechanism
    try {
      await dispatch(
        addCartProduct({
          productData: {
            identifier,
            payload: {
              duration: 12,
              duration_unit: 'm',
              // $FlowFixMe[prop-missing]
              domain: {
                fqdn,
                tld,
              },
              shopping: {
                studio: true,
              },
            },
            process: 'renew',
            product_type: 'paas',
          },
        }),
      );

      if (current?.phone) {
        await dispatch(
          updateCartProductsOwnership({
            filter: [{ productType: 'paas', process: 'renew' }],
          }),
        );
      }
      await dispatch(getCart());

      const freshCartId = dispatch((_: Dispatch, getState: GetState) =>
        selectCartUUID(getState()),
      );

      const step = getStepAfterDomainChoice({
        isStudioUser,
        flag: studioFirstBuyFlagActivate,
        hasPhone: !!current?.phone,
        hasAddress: !missingOrgInfo,
        hasOtherProducts: cartTotalProducts > 0,
      });

      const stepUrl = getStepUrl(freshCartId)[step];
      navigate(stepUrl);
    } catch (e) {
      dispatch(notifyMaybeApiError(e));
    }
  };

  return (
    <Layout>
      <View
        padding={{ m: 28 }}
        gap={12}
        className={styles.root}
        attributes={{ ref: targetRef }}
      >
        <div>
          <DomainIntro />
        </div>
        <div>
          <DomainSearch handleChooseDomain={handleChooseDomain} />
        </div>
      </View>
    </Layout>
  );
}

export default (withPageMeta(({ __ }) => ({
  title: __('Gandi studio - Gandi.net'),
  description: __(
    'Find and register the perfect domain name for your project on gandi.net. Free search, first domain free for the first year',
  ),
}))(StudioConvert): React.ComponentType<{}>);
